import { z } from 'zod';
import { Role } from '../enums.js';
import { Email, IsoDateTime, NonEmptyName, Uuid } from '../primitives.js';

export const LoginRequest = z
  .object({
    email: Email,
    password: z.string().min(1).max(200),
    /** Which salon to act within (a user may belong to several). Optional → first membership. */
    salonId: Uuid.optional(),
  })
  .meta({ id: 'LoginRequest' });
export type LoginRequest = z.infer<typeof LoginRequest>;

export const RegisterRequest = z
  .object({
    email: Email,
    password: z.string().min(10).max(200),
    displayName: NonEmptyName,
    salonName: NonEmptyName,
    salonSlug: z
      .string()
      .min(2)
      .max(40)
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  })
  .meta({ id: 'RegisterRequest' });
export type RegisterRequest = z.infer<typeof RegisterRequest>;

/** The authenticated principal and the tenant context it resolves to. */
export const SessionUser = z
  .object({
    userId: Uuid,
    email: Email,
    displayName: NonEmptyName,
    salonId: Uuid,
    staffId: Uuid.nullable(),
    role: Role,
    renterId: Uuid.nullable().describe('non-null when acting as a chair renter'),
  })
  .meta({ id: 'SessionUser' });
export type SessionUser = z.infer<typeof SessionUser>;

export const Membership = z
  .object({
    salonId: Uuid,
    salonName: NonEmptyName,
    role: Role,
    renterId: Uuid.nullable(),
  })
  .meta({ id: 'Membership' });
export type Membership = z.infer<typeof Membership>;

// ── Optional TOTP 2FA (owner/manager; deferred WS6 TODO) ────────────────────────────────────────────
/** A 6-digit TOTP code, exactly as the authenticator app shows it. */
export const TotpCode = z
  .string()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator');
/** A single-use recovery code (letters + digits, dash optional). */
export const RecoveryCode = z.string().min(8).max(32);

/**
 * The login result. EITHER a full SessionUser (no 2FA, or already stepped up) OR a `twoFactorRequired`
 * challenge: the credentials were correct but the account is TOTP-enrolled, so NO session is issued
 * until POST /auth/2fa/verify succeeds with the returned `challengeId`.
 */
export const LoginResult = z
  .union([
    SessionUser,
    z.object({
      twoFactorRequired: z.literal(true),
      challengeId: Uuid.describe('opaque pending-login id; pass to /auth/2fa/verify'),
    }),
  ])
  .meta({ id: 'LoginResult' });
export type LoginResult = z.infer<typeof LoginResult>;

/** Complete a 2FA step-up: a TOTP code OR a single-use recovery code (exactly one). */
export const TotpVerifyRequest = z
  .object({
    challengeId: Uuid,
    code: TotpCode.optional(),
    recoveryCode: RecoveryCode.optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Provide either a TOTP code or a recovery code',
  })
  .meta({ id: 'TotpVerifyRequest' });
export type TotpVerifyRequest = z.infer<typeof TotpVerifyRequest>;

/** Current 2FA state for the signed-in account (drives the settings UI). */
export const TotpStatus = z
  .object({
    enrolled: z.boolean(),
    enrolledAt: IsoDateTime.nullable(),
    recoveryCodesRemaining: z.number().int().nonnegative(),
  })
  .meta({ id: 'TotpStatus' });
export type TotpStatus = z.infer<typeof TotpStatus>;

/** Begin enrollment: a fresh (not-yet-enabled) secret to add to an authenticator app. */
export const TotpEnrollBegin = z
  .object({
    secret: z.string().describe('base32 secret for manual entry'),
    otpauthUri: z
      .string()
      .describe('otpauth:// provisioning URI (render as text — no QR dependency)'),
  })
  .meta({ id: 'TotpEnrollBegin' });
export type TotpEnrollBegin = z.infer<typeof TotpEnrollBegin>;

/** Confirm enrollment by proving a live code; the secret is encrypted + enabled on success. */
export const TotpEnrollVerifyRequest = z
  .object({ code: TotpCode })
  .meta({ id: 'TotpEnrollVerifyRequest' });
export type TotpEnrollVerifyRequest = z.infer<typeof TotpEnrollVerifyRequest>;

/** Returned ONCE when 2FA is enabled: the recovery codes (never shown again). */
export const TotpEnabled = z
  .object({ recoveryCodes: z.array(z.string()) })
  .meta({ id: 'TotpEnabled' });
export type TotpEnabled = z.infer<typeof TotpEnabled>;

/** Disable 2FA — requires re-proof: a fresh TOTP code OR the account password. */
export const TotpDisableRequest = z
  .object({
    code: TotpCode.optional(),
    password: z.string().min(1).max(200).optional(),
  })
  .refine((v) => Boolean(v.code) || Boolean(v.password), {
    message: 'Provide a current TOTP code or your password',
  })
  .meta({ id: 'TotpDisableRequest' });
export type TotpDisableRequest = z.infer<typeof TotpDisableRequest>;

/** Personal access token / OAuth-style token for the public API (ADR-0006). */
export const CreatePat = z
  .object({
    name: NonEmptyName,
    scopes: z.array(z.string()).default(['read']),
    expiresAt: IsoDateTime.nullish(),
  })
  .meta({ id: 'CreatePat' });
export type CreatePat = z.infer<typeof CreatePat>;

export const PatCreated = z
  .object({
    id: Uuid,
    name: NonEmptyName,
    token: z.string().describe('shown once at creation; only a hash is stored'),
    scopes: z.array(z.string()),
    expiresAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PatCreated' });
export type PatCreated = z.infer<typeof PatCreated>;

/**
 * Canonical public-API scopes for personal access tokens (Phase 4). Session-cookie principals are
 * unrestricted; scopes govern Bearer-PAT access only. Two coarse super-scopes simplify common
 * tokens: `read` ⊇ every `*:read`, and `write` ⊇ every `*:write` AND every read (see
 * `core/scopes.ts` for the containment logic). PAT `scopes` columns stay free-form `string[]` for
 * backward compatibility — this enum is the recommended vocabulary, not a hard DB constraint.
 */
export const ApiScope = z.enum([
  'read',
  'write',
  'clients:read',
  'clients:write',
  'appointments:read',
  'appointments:write',
  'catalog:read',
  'catalog:write',
  'queue:read',
  'queue:write',
  'payments:read',
  'payments:write',
  'comms:read',
  'comms:send',
  'stock:read',
  'stock:write',
  'reports:read',
  'webhooks:manage',
]);
export type ApiScope = z.infer<typeof ApiScope>;

/** The full scope list, for docs/UX and validation helpers. */
export const SCOPES: readonly ApiScope[] = ApiScope.options;
